import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { formatTimeStamp } from '../utils/dates'
import { Allocation, AllocationConstructorArgs } from './Allocation'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { Option } from './Option'
import { FKType } from './SQLBuilder'
import { User } from './User'

type ScheduleConstructorArgs = Omit<
    ConstructorArgs<() => Schedule>,
    'date_values' | 'employee_values' | 'resource_values'
> & {
    date_values: moment.Moment[] | string[] | '' | null
    employee_values: User[][] | string[][] | '' | null
    resource_values: Allocation[][] | AllocationConstructorArgs[][] | '' | null
}

export class Schedule extends BaseRecord {
    readonly recordType = 'schedule'

    static readonly SCHEDULE_CAPACITY = 60

    dow?: number // Integer
    wom?: number // Integer
    frequency?: string
    interval?: number // Integer
    instances?: number // Integer
    dateValues: moment.Moment[] = []
    employeeValues: User[][] = []
    resourceValues: Allocation[][] = []

    rate?: number
    deposit?: number
    duration?: number // Integer

    date?: moment.Moment
    endDate?: moment.Moment
    cancelledDate?: moment.Moment
    confirmedDate?: moment.Moment
    paidDepositDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<ScheduleConstructorArgs>) {
        super(data)
        if (!data) {
            const me = PSApplication.me()
            this.frequency = 'weekly'
            this.creator = me
            this.owner = me.owner
            return
        }

        this.dow = this.propertyFromIntArg(data.dow)
        this.wom = this.propertyFromIntArg(data.wom)
        this.frequency = data.frequency ?? undefined
        this.interval = this.propertyFromIntArg(data.interval)
        this.instances = this.propertyFromIntArg(data.instances)
        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.deposit = this.propertyFromCurrencyArg(data.deposit)
        this.date = this.propertyFromTimestampArg(data.date)
        this.endDate = this.propertyFromTimestampArg(data.end_date)
        this.dateValues = (data.date_values || []).map((data) =>
            typeof data === 'string' ? moment(data) : data
        )
        this.employeeValues = (data.employee_values || []).map((dataRow) =>
            dataRow.map((data) =>
                data instanceof User
                    ? data
                    : (CacheFactory.getInstance().getRecord(User, data) ?? User.initWithId(data))
            )
        )

        this.resourceValues = (data.resource_values || []).map((dataRow) =>
            dataRow.map((data) => (data instanceof Allocation ? data : new Allocation(data)))
        )
        this.duration = this.propertyFromIntArg(data.duration)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<ScheduleConstructorArgs>): Schedule {
        return new Schedule({ ...this.serializeForClone(), ...overrides })
    }

    static dayString(day: number, shortDay: boolean): string | undefined {
        const daysArray = Schedule.dayStrings()
        for (const option of daysArray) {
            if (option.id === day.toString())
                return shortDay ? option.label!.substring(0, 3) : option.label
        }
        return undefined
    }

    static dayStrings(): Option[] {
        const days: Option[] = []
        days.push(new Option(undefined, { id: '1', label: 'Monday' }))
        days.push(new Option(undefined, { id: '2', label: 'Tuesday' }))
        days.push(new Option(undefined, { id: '4', label: 'Wednesday' }))
        days.push(new Option(undefined, { id: '8', label: 'Thursday' }))
        days.push(new Option(undefined, { id: '16', label: 'Friday' }))
        days.push(new Option(undefined, { id: '32', label: 'Saturday' }))
        days.push(new Option(undefined, { id: '64', label: 'Sunday' }))
        return days
    }

    static weekMonthStrings(date: moment.Moment | undefined): Option[] {
        const days: Option[] = []
        const l = Schedule.weekDaysFromDate(date)
        const dayString = Schedule.dayString(parseInt(l[0]), false)?.toLowerCase()
        days.push(new Option(undefined, { id: '1', label: `First ${dayString}` }))
        days.push(new Option(undefined, { id: '2', label: `Second ${dayString}` }))
        days.push(new Option(undefined, { id: '3', label: `Third ${dayString}` }))
        days.push(new Option(undefined, { id: '4', label: `Fourth ${dayString}` }))
        days.push(new Option(undefined, { id: '-1', label: `Last ${dayString}` }))
        days.push(new Option(undefined, { id: '-2', label: `Second to last ${dayString}` }))
        return days
    }

    static intervalStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '1', label: 'Every week' }))
        strings.push(new Option(undefined, { id: '2', label: 'Every 2 weeks' }))
        strings.push(new Option(undefined, { id: '3', label: 'Every 3 weeks' }))
        strings.push(new Option(undefined, { id: '4', label: 'Every 4 weeks' }))
        strings.push(new Option(undefined, { id: '1m', label: 'Every month' }))
        strings.push(new Option(undefined, { id: '5', label: 'Every 5 weeks' }))
        strings.push(new Option(undefined, { id: '6', label: 'Every 6 weeks' }))
        strings.push(new Option(undefined, { id: '7', label: 'Every 7 weeks' }))
        strings.push(new Option(undefined, { id: '8', label: 'Every 8 weeks' }))
        strings.push(new Option(undefined, { id: '2m', label: 'Every 2 months' }))
        strings.push(new Option(undefined, { id: '9', label: 'Every 9 weeks' }))
        strings.push(new Option(undefined, { id: '10', label: 'Every 10 weeks' }))
        strings.push(new Option(undefined, { id: '11', label: 'Every 11 weeks' }))
        strings.push(new Option(undefined, { id: '12', label: 'Every 12 weeks' }))
        strings.push(new Option(undefined, { id: '3m', label: 'Every 3 months' }))
        strings.push(new Option(undefined, { id: '6m', label: 'Every 6 months' }))
        strings.push(new Option(undefined, { id: '8m', label: 'Every 8 months' }))
        strings.push(new Option(undefined, { id: '10m', label: 'Every 10 months' }))
        strings.push(new Option(undefined, { id: '12m', label: 'Every 12 months' }))
        return strings
    }

    static timezoneStrings(): Option[] {
        return [
            new Option(undefined, { id: 'America/New_York', label: 'Eastern Time' }),
            new Option(undefined, { id: 'America/Los_Angeles', label: 'Pacific Time' }),
            new Option(undefined, { id: 'America/Chicago', label: 'Central Time' }),
            new Option(undefined, { id: 'America/Denver', label: 'Mountain Time' }),
            new Option(undefined, { id: 'America/Phoenix', label: 'Arizona Time' }),
            new Option(undefined, { id: 'America/Anchorage', label: 'Alaska Time' }),
            new Option(undefined, { id: 'Pacific/Honolulu', label: 'Hawaii Time' }),
        ]
    }

    static instanceStrings(limit: number): Option[] {
        const appointmentStrings: Option[] = []
        appointmentStrings.push(new Option(undefined, { id: '', label: 'Repeat forever' }))
        for (let i = 2; i <= 24; i++)
            if (i <= limit)
                appointmentStrings.push(
                    new Option(undefined, { id: i.toString(), label: i.toString() })
                )
        for (let i = 30; i <= 200; i += 10)
            if (i <= limit)
                appointmentStrings.push(
                    new Option(undefined, { id: i.toString(), label: i.toString() })
                )
        return appointmentStrings
    }

    get description(): string {
        return Schedule.describe(
            this.frequency!,
            this.weekDays,
            this.wom,
            this.interval,
            this.instances,
            this.dateValues
        )
    }

    static describe(
        freq: string,
        weekDays: string[],
        weekMonth: number | undefined,
        interval: number | undefined,
        instances: number | undefined,
        dates: moment.Moment[]
    ): string {
        if (dates && dates.length > 0)
            return instances === 1
                ? 'Custom schedule, 1 session'
                : `Custom schedule, ${instances} sessions`

        const workDays = weekDays
        const workDayStrings: string[] = []

        for (const dayString of workDays) {
            const lblString = Schedule.dayString(parseInt(dayString), true)
            if (lblString) workDayStrings.push(lblString)
        }
        const keyString = interval?.toString()
        if (weekMonth !== undefined && weekMonth !== 0) {
            const prefixString =
                weekMonth === -2
                    ? 'Second to last'
                    : weekMonth === -1
                      ? 'Last'
                      : weekMonth === 1
                        ? 'First'
                        : weekMonth === 2
                          ? 'Second'
                          : weekMonth === 3
                            ? 'Third'
                            : 'Fourth'
            const endingString =
                keyString === '1'
                    ? 'each'
                    : keyString === '2'
                      ? 'every other'
                      : keyString === '3'
                        ? 'every 3rd'
                        : `every ${keyString}th`
            return `${prefixString} ${workDayStrings.join(', ').toLowerCase()} of ${endingString} month`
        }
        else {
            let intervalString = ''
            const daysString = workDayStrings.join(', ')
            for (const option of Schedule.intervalStrings()) {
                if (!intervalString && option.id === keyString)
                    if (parseInt(option.id!) > 1)
                        intervalString = `every ${option.id} ${freq === 'weekly' ? 'weeks' : 'months'}`
                    else intervalString = `every ${freq === 'weekly' ? 'week' : 'month'}`
            }
            if (workDayStrings.length > 0 && intervalString)
                return `${daysString} ${intervalString}`
            else if (workDayStrings.length > 0) return daysString
            return intervalString
        }
    }

    get weekDays(): string[] {
        const l: string[] = []
        const dow = this.dow ?? 0
        for (let i = 0; i < 7; i++) if ((dow & (1 << i)) !== 0) l.push((1 << i).toString())
        return l
    }

    set weekDays(weekDays: string[]) {
        let dow = 0
        for (const weekDay of weekDays) dow |= parseInt(weekDay)
        this.dow = dow
    }

    setWeekDaysFromDate(date: moment.Moment | undefined) {
        if (!date) date = moment()
        this.weekDays = Schedule.weekDaysFromDate(date)
    }

    static weekDaysFromDate(date: moment.Moment | undefined): string[] {
        if (!date) date = moment()
        const dayOfWeek = date.day()
        return [(1 << (dayOfWeek > 0 ? dayOfWeek - 1 : 6)).toString()]
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.dateValues) this.dateValues = data.dateValues.map((date: any) => moment(date))
        if (data.employeeValues)
            this.employeeValues = data.employeeValues.map((userList: any) =>
                userList.map((user: any) => BaseRecord.fromSerializer(user, User))
            )
        if (data.resourceValues)
            this.resourceValues = data.resourceValues.map((allocationList: any) =>
                allocationList.map((allocation: any) => new Allocation(allocation))
            )
        if (data.date) this.date = moment(data.date)
        if (data.endDate) this.endDate = moment(data.endDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.confirmedDate) this.confirmedDate = moment(data.confirmedDate)
        if (data.paidDepositDate) this.paidDepositDate = moment(data.paidDepositDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator', 'employeeValues'],
    }

    static dataType(): DataType {
        return 'schedules'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['date'] = formatTimeStamp(this.date)
        dict['duration'] = this.duration?.toString() ?? ''
        dict['rate'] = this.rate?.toString() ?? ''
        dict['deposit'] = this.deposit?.toString() ?? ''
        dict['dow'] = this.dow?.toString() ?? ''
        dict['wom'] = this.wom?.toString() ?? ''
        if (this.frequency) dict['frequency'] = this.frequency
        dict['interval'] = this.interval?.toString() ?? ''
        dict['instances'] = this.instances?.toString() ?? ''
        dict['date_values'] = this.dateValues ?? ''
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
